import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaShieldAlt } from "react-icons/fa";
import { apihubPost } from "../../api/apihub";
import { businessPath } from "../../config/businessMenu";
import {
  getAssignablePermissionModules,
  permissionKey,
  sanitizeAssignablePermissions,
} from "../../config/teamRolePermissions";
import { useProjectPhone } from "../../hooks/useProjectPhone";
import { getUserType } from "../../utils/auth";

const formatAction = (action) =>
  String(action || "")
    .replace(/_/g, " ")
    .replace(/\+/g, " + ");

const formatModuleLabel = (label) =>
  String(label || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/whatsApp/i, "WhatsApp")
    .replace(/Opt-in/i, "Opt-in ");

const CreateTeamRole = () => {
  const { projectId } = useProjectPhone();
  const navigate = useNavigate();
  const userType = getUserType();
  const permissionModules = useMemo(
    () => getAssignablePermissionModules(userType),
    [userType],
  );
  const [form, setForm] = useState({
    teamRoleName: "",
    extractedalphaid: projectId,
    permissions: {},
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const selectedCount = useMemo(
    () => Object.values(form.permissions).filter(Boolean).length,
    [form.permissions],
  );

  const togglePermission = (key) => {
    setForm((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: !prev.permissions[key],
      },
    }));
  };

  const setModulePermissions = (mod, enabled) => {
    setForm((prev) => {
      const next = { ...prev.permissions };
      mod.actions.forEach((action) => {
        next[permissionKey(mod.label, action)] = enabled;
      });
      return { ...prev, permissions: next };
    });
  };

  const moduleSelectedCount = (mod) =>
    mod.actions.filter((action) =>
      Boolean(form.permissions[permissionKey(mod.label, action)]),
    ).length;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.teamRoleName.trim()) {
      setError("Please enter a team role name.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await apihubPost("/getInteractedCustomerDetail/create-team-role", {
        ...form,
        extractedalphaid: projectId,
        permissions: sanitizeAssignablePermissions(form.permissions, userType),
      });
      navigate(businessPath(projectId, "teamroles"));
    } catch (err) {
      setError(err.message || "Failed to create team role");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="biz-panel-page biz-teamrole-create-page">
      <div className="biz-panel-header biz-panel-header-row">
        <div>
          <h1>
            <FaShieldAlt />
            Create Team Role
          </h1>
          <p className="biz-panel-subtitle">
            Name the role, then choose which modules agents can access.
          </p>
        </div>
        <Link
          to={businessPath(projectId, "teamroles")}
          className="biz-btn-secondary"
        >
          <FaArrowLeft /> Back
        </Link>
      </div>

      {error ? <p className="biz-banner biz-banner-error">{error}</p> : null}

      <form className="biz-teamrole-create" onSubmit={onSubmit}>
        <section className="biz-teamrole-create-name">
          <label htmlFor="team-role-name">
            Role name
            <span aria-hidden="true"> *</span>
          </label>
          <input
            id="team-role-name"
            type="text"
            value={form.teamRoleName}
            onChange={(e) =>
              setForm((f) => ({ ...f, teamRoleName: e.target.value }))
            }
            placeholder="e.g. Support Agent, Campaign Manager"
            required
            autoFocus
          />
        </section>

        <section className="biz-teamrole-create-perms">
          <div className="biz-teamrole-create-perms-head">
            <div>
              <h2>Permissions</h2>
              <p>Toggle access per module. Use Select all on a module to grant every action.</p>
            </div>
            <span className="biz-teamrole-create-count">
              {selectedCount} selected
            </span>
          </div>

          <div className="biz-perm-grid">
            {permissionModules.map((mod) => {
              const selected = moduleSelectedCount(mod);
              const allOn = selected === mod.actions.length;
              return (
                <article
                  key={mod.label}
                  className={`biz-perm-card${selected > 0 ? " is-active" : ""}`}
                >
                  <header className="biz-perm-card-head">
                    <div>
                      <strong>{formatModuleLabel(mod.label)}</strong>
                      <span>
                        {selected}/{mod.actions.length}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="biz-perm-card-toggle"
                      onClick={() => setModulePermissions(mod, !allOn)}
                    >
                      {allOn ? "Clear" : "Select all"}
                    </button>
                  </header>
                  <div className="biz-perm-card-actions">
                    {mod.actions.map((action) => {
                      const key = permissionKey(mod.label, action);
                      return (
                        <label key={key} className="biz-perm-chip">
                          <input
                            type="checkbox"
                            checked={Boolean(form.permissions[key])}
                            onChange={() => togglePermission(key)}
                          />
                          <span>{formatAction(action)}</span>
                        </label>
                      );
                    })}
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <div className="biz-teamrole-create-actions">
          <Link
            to={businessPath(projectId, "teamroles")}
            className="biz-btn-secondary"
          >
            Cancel
          </Link>
          <button type="submit" className="biz-btn-primary" disabled={saving}>
            {saving ? "Creating…" : "Create role"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateTeamRole;
